/**
 * Zod-v4-aware React Hook Form resolver (issue #195).
 *
 * `@hookform/resolvers@3`'s shipped `zodResolver` reads `error.errors` to
 * detect a `ZodError`, but Zod v4 renamed that to `error.issues` — so the
 * canonical resolver mis-classifies every validation failure and re-throws
 * it as an unhandled rejection. Forms with `mode: "onSubmit"` then silently
 * swallow it (no field errors surfaced); forms with `mode: "onChange"`
 * spam the console on every keystroke.
 *
 * This adapter maps `issue.path` into RHF's `FieldErrors` shape via the
 * dot-joined path. First-issue-wins on collision so the operator sees the
 * most-actionable message per field rather than a noisy stack.
 *
 * Drop this whole file once the upstream package is bumped to a release
 * that supports Zod v4 (see #195 for the tracking note).
 *
 * Typing: Zod v4 dropped the `ZodTypeDef` second generic of `z.ZodType` and
 * the inferred shape carries internal `$ZodObjectInternals` types that
 * don't satisfy RHF's `Resolver<T>` constraint. We accept `unknown` for the
 * schema parameter and cast at the boundary — runtime safety comes from
 * `safeParseAsync` itself.
 */

import type { FieldErrors, FieldValues, Resolver } from "react-hook-form";

type ParseableSchema<T> = {
  safeParseAsync(input: unknown): Promise<
    | { success: true; data: T }
    | {
        success: false;
        error: { issues: Array<{ path: PropertyKey[]; code?: string; message: string }> };
      }
  >;
};

export function makeZodResolver<T extends FieldValues>(schema: ParseableSchema<T>): Resolver<T> {
  return async (values) => {
    const parsed = await schema.safeParseAsync(values);
    if (parsed.success) {
      return { values: parsed.data, errors: {} };
    }
    const errors: FieldErrors<T> = {};
    const sink = errors as Record<string, { type: string; message: string }>;
    for (const issue of parsed.error.issues) {
      const path = issue.path.join(".");
      if (path && !(path in sink)) {
        sink[path] = {
          type: issue.code ?? "validation",
          message: issue.message,
        };
      }
    }
    return { values: {}, errors };
  };
}
